import React, { useEffect } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { RadioInput } from 'components/form';
import { StarRating } from 'components';
import { toTitleCase } from 'utils/titleCase';
import * as yup from 'yup';
import { BaseEditModal } from '../base-edit-modal';

type SoftSkillFormData = {
  softSkills: {
    title: string;
    description: string;
    checked: boolean;
    rating: number;
  }[];
};

const softSkillSchema = yup.object().shape({
  softSkills: yup.array().of(
    yup.object().shape({
      title: yup.string().required('Skill name is required'),
      description: yup.string().required('Description is required'),
      checked: yup.boolean().default(false),
      rating: yup.number().min(1, 'Rating must be at least 1').max(5, 'Rating must be at most 5').required('Rating is required'),
    })
  )
    .required('At least one soft skill is required')
    .min(1, 'At least one soft skill is required')
    .test(
      'max-checked',
      'You can select a maximum of 5 skills',
      (skills) => skills.filter(skill => skill.checked).length <= 5
    ),
});

interface SoftSkillsEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentData: SoftSkillFormData;
  onSave: (data: SoftSkillFormData) => void;
  isSubmitting?: boolean;
}

export const SoftSkillsEditModal: React.FC<SoftSkillsEditModalProps> = ({
  isOpen,
  onClose,
  currentData,
  onSave,
  isSubmitting
}) => {
  const defaultValues: SoftSkillFormData = {
    softSkills: currentData?.softSkills || [],
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    reset,
    setValue,
    watch
  } = useForm<SoftSkillFormData>({
    defaultValues,
    resolver: yupResolver(softSkillSchema)
  });

  const { fields } = useFieldArray({
    control,
    name: 'softSkills'
  });

  useEffect(() => {
    reset(defaultValues);
  }, [currentData, reset]);

  const onSubmit = (data: SoftSkillFormData) => {
    onSave(data);
  };

  const handleRatingChange = (index: number, rating: number) => {
    setValue(`softSkills.${index}.rating`, rating, { shouldValidate: true });
  };

  const checkedCount = watch('softSkills').filter(skill => skill.checked).length;

  return (
    <BaseEditModal
      isOpen={isOpen}
      onClose={onClose}
      title="Soft Skills"
      onSubmit={handleSubmit(onSubmit)}
      isSubmitting={isSubmitting}
    >
      <div className="font-[Gelion] w-[90%] mx-auto mt-[10px] mb-[30px]">
        <p className="mb-5 pb-2.5 border-b border-[#D0D5DD]">
          You can select any of the Soft skills that best resonate with you (Max of 5 selections)
        </p>

        <h3 className="text-lg font-semibold text-[#1D2939] mb-3">Soft Skills</h3>

        {errors.softSkills && typeof errors.softSkills === 'object' && 'message' in errors.softSkills && (
          <small className="text-xs text-[#e74444] block mb-3">{errors.softSkills.message as string}</small>
        )}

        <div className="grid grid-cols-2 gap-x-[35px] gap-y-[20px]">
          {fields.map((skill, index) => (
            <div key={skill.id}>
              <RadioInput
                register={register}
                name={`softSkills.${index}.checked`}
                validatorMessage={errors.softSkills?.[index]?.checked?.message}
                options={[{
                  label: toTitleCase(skill.title),
                  description: skill.description,
                  value: 'true',
                }]}
                type="checkbox"
                parentClassName="grid gap-[5%] !mb-0"
                labelClassName='!mb-1'
                disabled={checkedCount >= 5 && !watch(`softSkills.${index}.checked`)}
              />
              {watch(`softSkills.${index}.checked`) && (
                <div className="flex mt-1 ml-[50px]">
                  <StarRating
                    value={watch(`softSkills.${index}.rating`)}
                    onChange={(rating) => handleRatingChange(index, rating)}
                    count={5}
                    startAt={1}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </BaseEditModal>
  );
};
